import { Component, Vue } from 'vue-property-decorator';
import lodash from 'lodash';
import { sumByField, tableHeight } from '@/helpers';
import { AssessmentPeriodDTO, ProjectDTO, SelectionOptions, TableHeaderColumns, UserEngagementDTO } from '@/models';
import { fetchAssessmentPeriods, fetchProjectsOfCurrentUser, getUserDisplayInfo } from '@/services';
import ExportByProject from '@/services/export-by-project';
import Avatar from '@/components/common/avatar/Avatar.vue';
import FilterDropdown from '@/components/common/filter-dropdown/FilterDropdown.vue';
import SearchInput from '@/components/common/search-input/SearchInput.vue';
import Pagination from '@/components/common/pagination/Pagination.vue';
import InlineLoader from '@/components/common/loader/inline-loader/InlineLoader.vue';

@Component({
  components: {
    Avatar,
    FilterDropdown,
    SearchInput,
    Pagination,
    InlineLoader
  }
})
export default class ProjectStatistics extends Vue {
  private allAssessmentPeriods: AssessmentPeriodDTO[] = [];
  private periodOptions: SelectionOptions = [];
  private managerOptions: SelectionOptions = [];
  private searchProject = '';
  private selectedPeriods = [] as string[];
  private selectedManagers = [] as string[];
  private page = 1;
  private pageCount = 0;
  private itemsPerPage = 10;

  private projects: ProjectDTO[] = [];
  private isFetchingProjects = true;
  private selectedRows: ProjectDTO[] = [];

  get tableHeight(): string {
    return tableHeight(this.pageCount);
  }

  get tableHeaderColumns(): TableHeaderColumns {
    return [
      {
        text: '#',
        value: 'index'
      },
      {
        text: 'ID',
        value: 'id'
      },
      {
        text: 'Project Name',
        value: 'name',
        class: 'grey--text'
      },
      {
        text: 'Member Count',
        value: 'memberCount',
        class: 'grey--text '
      },
      {
        text: 'Total Pollens',
        value: 'totalDrops',
        class: 'grey--text'
      }
    ];
  }

  get textExportButton(): string {
    if (!this.selectedRows.length || this.selectedRows.length === this.displayProjects.length) {
      return 'Export all';
    }
    return this.selectedRows.length === 1 ? `Export 1 project` : `Export ${this.selectedRows.length} projects`;
  }

  filterByUser<T>(value: keyof T, search: string, item: ProjectDTO): boolean {
    return (
      value !== null &&
      search !== null &&
      (item.name.toLowerCase().includes(search.toLowerCase()) || item.id.toString().includes(search))
    );
  }

  async exportProject(): Promise<void> {
    const assessmentPeriods = await this.getProjectPeriodsInformation();
    const projectToExport = this.selectedRows.length ? this.selectedRows : this.displayProjects;
    const assessmentPeriodsExport = new ExportByProject(assessmentPeriods, projectToExport);
    await assessmentPeriodsExport.generateXLSXFile();
  }

  onSearch(searchText: string): void {
    this.searchProject = searchText;
  }

  onManagerChange(selectedManagers: string[]): void {
    this.selectedManagers = selectedManagers;
  }

  onPeriodChange(selectedPeriods: string[]): void {
    this.selectedPeriods = selectedPeriods;
  }

  getProjectPeriodsInformation(): AssessmentPeriodDTO[] {
    if (this.selectedPeriods.length) {
      return this.allAssessmentPeriods.filter(
        period =>
          this.selectedPeriods.filter(item => {
            if (period.id) {
              return item === period?.id.toString();
            }
          }).length > 0
      );
    }
    return this.allAssessmentPeriods;
  }

  get displayProjects(): ProjectDTO[] {
    const { projects, selectedManagers, selectedPeriods } = this;
    let result = projects.map(project => {
      return { ...project };
    });

    if (selectedManagers.length) {
      result = result.filter(project => selectedManagers.includes('' + project.manager?.id));
    }
    if (selectedPeriods.length) {
      result = result.filter(project => {
        project.projectAssessments = project.projectAssessments.filter(assessment => {
          return selectedPeriods.includes('' + assessment.assessmentPeriodId);
        });
        return project.projectAssessments.length !== 0;
      });
    }
    result.forEach(project => {
      let userList: UserEngagementDTO[] = [];
      project.projectAssessments.forEach(assessment => {
        userList = userList.concat(assessment.userEngagements);
      });
      project.memberCount = lodash.uniqBy(userList, 'user.id').length;
    });
    result = result.map((project, idx) => {
      let totalDrops = sumByField(project.projectAssessments, 'finalDrops');
      if (!totalDrops) {
        totalDrops = project.projectAssessments.reduce(
          (acc, curr) => acc + sumByField(curr.userEngagements, 'individualDrops'),
          0
        );
      }
      return {
        ...project,
        index: idx + 1,
        totalDrops
      };
    });
    return result;
  }

  async created(): Promise<void> {
    const { data: projects } = await fetchProjectsOfCurrentUser();
    this.projects = projects;
    this.isFetchingProjects = false;

    const { data: allAssessmentPeriods } = await fetchAssessmentPeriods();
    this.allAssessmentPeriods = allAssessmentPeriods;
    allAssessmentPeriods.map(item => {
      this.periodOptions.push({ text: item.name, value: item.id?.toString() || '-1' });
    });
    this.projects.map(project =>
      this.managerOptions.push({
        text: getUserDisplayInfo(project.manager),
        value: '' + project.manager?.id
      })
    );
    this.managerOptions = lodash.uniqBy(this.managerOptions, 'value');
  }

  onPageChange(page: number): void {
    this.page = page;
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.itemsPerPage = itemsPerPage;
  }

  selectAll(): void {
    this.selectedRows = this.selectedRows.length === this.displayProjects.length ? [] : this.displayProjects;
  }
}
